import {
  Contact,
  ContactEmail,
  ContactLink,
  Currency,
  Duration,
  GrantApplication,
} from '@/__generated__/graphql';
import { logError } from '@/lib/rum';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { GRANT_DURATION_DISPLAY_TEXT } from '@/consts/grant/dashboard';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isValidUrl = (urlString?: string) => {
  if (!urlString) {
    return null;
  }
  var urlPattern = new RegExp(
    '^(https?:\\/\\/)?' + // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
};

export const isValidEmail = (email: string) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailPattern.test(email);
};

export const copyToClipboard = async (text: string | undefined | null) => {
  if (!text) {
    return null;
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    logError('Failed to copy text.', err);
  }
};

type UpdateParamActionType = 'add' | 'remove';

export function updateUrlParam(
  param: string,
  value: string | null,
  type: UpdateParamActionType = 'add'
) {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);

  if (type === 'add' && value !== null) {
    if (searchParams.has(param)) {
      searchParams.set(param, value);
    } else {
      searchParams.append(param, value);
    }
  } else if (type === 'remove') {
    searchParams.delete(param);
  }

  const newUrl = `${url.origin}${url.pathname}?${searchParams.toString()}`;

  window.history.pushState({ path: newUrl }, '', newUrl);
}

export function getUrlParams(params: string | string[]) {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);

  if (Array.isArray(params)) {
    const obj: any = {};

    for (const key of params) {
      obj[key] = searchParams.get(key);
    }
    return obj;
  }

  return searchParams.get(params);
}

export const isDayDisabled = (day: Date) => {
  const isWednesday = (day: Date) => {
    return day.getDay() === 3;
  };

  const isFutureDate = (day: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return day.getTime() > today.getTime();
  };

  if (!isFutureDate(day)) {
    return false;
  }

  return !isWednesday(day);
};

export const calculateNearestFutureWednesday = () => {
  const today = new Date();
  let nextWednesday = new Date();

  nextWednesday.setDate(today.getDate() + ((3 + 7 - today.getDay()) % 7));
  nextWednesday.setUTCHours(0, 0, 0, 0);

  // If the next Wednesday is today, get the following Wednesday
  if (nextWednesday.getTime() === today.getTime()) {
    nextWednesday.setDate(nextWednesday.getDate() + 7);
  }

  return nextWednesday;
};

export const getAvgAmount = (
  currency: Currency | undefined,
  number: number | undefined,
  notation: 'compact' | 'standard' = 'standard'
) => {
  if (!currency || !number) {
    return null;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    notation: notation,
  }).format(number);
};

export const findClosestDateString = (
  dateStrings?: string[] | null,
  format?: boolean
) => {
  if (!dateStrings?.length) {
    return null;
  }
  const today: any = new Date();

  const futureDateStrings = dateStrings.filter((dateString) => {
    const date = new Date(dateString);
    return date > today;
  });

  if (futureDateStrings.length === 0) {
    return null;
  }

  let closestDateString;
  let minDifference = Infinity;

  for (const dateString of futureDateStrings) {
    const date: any = new Date(dateString);
    const difference = date - today;

    if (difference < minDifference) {
      minDifference = difference;
      closestDateString = dateString;
    }
  }

  if (format) {
    return formatDateString(closestDateString);
  }

  return closestDateString;
};

export const formatDateString = (dateString?: string | null) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
};

export const isValidDateString = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return !isNaN(date.valueOf());
};

export const splitCamelCaseToString = (s: string) => {
  return s.split(/(?=[A-Z])/).join(' ');
};

export const getCurrencySymbol = (currency?: Currency) => {
  if (!currency) {
    return null;
  }
  switch (currency.toUpperCase()) {
    case 'ILS':
      return '₪';
    case 'USD':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return currency;
  }
};

export const getEmailFromContacts = (
  contacts: Contact[]
): ContactEmail | null => {
  if (!contacts || !Array.isArray(contacts)) {
    return null;
  }
  const emailContact = contacts.find(isContactEmail);
  return emailContact || null;
};

export const getLinkFromContacts = (
  contacts: Contact[]
): ContactLink | null => {
  if (!contacts || !Array.isArray(contacts)) {
    return null;
  }
  const linkContacts = contacts.filter(
    (contact) => isContactLink(contact) && isValidUrl(contact.link)
  ) as ContactLink[];
  return linkContacts[0] || null;
};

export function isContactEmail(contact: Contact): contact is ContactEmail {
  return contact.__typename === 'ContactEmail';
}

export function isContactLink(contact: Contact): contact is ContactLink {
  return contact.__typename === 'ContactLink';
}

export function removeEmptyProperties(arr: any) {
  return arr.map((obj: any) => {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined && value !== '') {
        newObj[key] = value;
      }
    }
    return newObj;
  });
}

export const getUserDateInUTC = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

export const cleanTypeName = (data: unknown) => {
  if (!data) {
    return null;
  }
  const omitTypename = (key: string, value: unknown) =>
    key === '__typename' ? undefined : value;
  return JSON.parse(JSON.stringify(data), omitTypename);
};

export const setCookieWithExpiry = (
  name: string,
  value: string,
  expiryMinutes: number
) => {
  const date = new Date();
  date.setTime(date.getTime() + expiryMinutes * 60 * 1000);
  const expires = 'expires=' + date.toUTCString();
  document.cookie = name + '=' + value + ';' + expires + ';path=/';
};

export const getCookie = (name: string) => {
  return document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');
};

export const formatToISOString = (inputDate: Date | undefined | null) => {
  if (!inputDate) {
    return null;
  }
  const date = new Date(inputDate);

  return date.toISOString();
};

export const formatToISOStringZeroHours = (inputDate?: Date) => {
  if (!inputDate) {
    return null;
  }
  const date = new Date(inputDate);
  const zeroHourDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  return zeroHourDate.toISOString();
};

export const capitalizeFirstLetter = (
  str: string | undefined | null
): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalize = (text?: string | null) => {
  if (!text) {
    return '';
  }
  if (typeof text !== 'string' || text.length === 0) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};
export function camelCaseToTitle(key?: string) {
  if (!key) {
    return null;
  }

  const separatedWords = key.replace(/([A-Z])/g, ' $1');
  return separatedWords.charAt(0).toUpperCase() + separatedWords.slice(1);
}

export function getHourFromDateTime(
  timestamp: string,
  timeZone?: string | null
) {
  const date = new Date(timestamp);
  const formattedTime = date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZone || undefined,
  });

  return formattedTime;
}
export function getFirstKeyUsingObjectKeys(obj: any) {
  if (!obj) {
    return undefined;
  }

  return Object.keys(obj)[0];
}

export function formatNumberToThousandsOrMillions(
  num?: number | null,
  decimals = 1
) {
  if (!num) {
    return undefined;
  }

  if (num >= 1000000) {
    return (num / 1000000).toFixed(2).replace(/\.00$/, '') + 'M';
  }

  if (num >= 1000) {
    return (num / 1000).toFixed(decimals).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
}

export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const formatAmount = (amount?: number) => {
  return amount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '';
};

export const formatDeadline = (
  deadline?: string[] | null,
  relevantDeadline?: string,
  duration?: Duration
) => {
  switch (duration) {
    case Duration.Rolling: {
      return GRANT_DURATION_DISPLAY_TEXT[duration];
    }
    case Duration.NotAcceptingProposalsAnymore: {
      return GRANT_DURATION_DISPLAY_TEXT[duration];
    }
    default: {
      const formattedDeadline =
        formatDateString(relevantDeadline) ||
        findClosestDateString(deadline, true);
      if (!formattedDeadline) {
        return 'Deadline has passed';
      }

      return formattedDeadline;
    }
  }
};

export const formatToUTC = (dateString: any) => {
  return dateString.split('T')[0] + 'T00:00:00Z';
};

export const handleOnlyNumber = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const isNumber = /[0-9]/.test(e.key);
  const isDecimal = e.key === '.' && !e.currentTarget.value.includes('.');
  const isControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
    e.key
  );

  if (!isNumber && !isDecimal && !isControl) {
    e.preventDefault();
  }
};

export const formatDeadlineTimeLeft = (
  deadline?: string[] | null,
  relevantDeadline?: string | null,
  updatedAt?: string | null
) => {
  const formattedDeadline =
    relevantDeadline || findClosestDateString(deadline, true);

  if (!formattedDeadline && !updatedAt) {
    return null;
  }

  return getDaysUntilDeadline(formattedDeadline, formatDateString(updatedAt));
};

const getDaysUntilDeadline = (
  formattedDeadline?: string | Date | null,
  updatedAt?: string | null
): string | null => {
  if (!formattedDeadline && !updatedAt) return null;

  const deadlineDate =
    formattedDeadline instanceof Date
      ? formattedDeadline
      : formattedDeadline && new Date(formattedDeadline);

  const now = new Date();

  // if (isNaN(deadlineDate.getTime())) {
  //   return null;
  // }

  const diffTime = (deadlineDate as Date)?.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (
    diffDays <= 7 &&
    diffDays > 0 &&
    !isNaN((deadlineDate as Date)?.getTime())
  ) {
    return `I will finish in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  } else if (updatedAt) {
    const updatedDate = new Date(updatedAt);
    if (isNaN(updatedDate.getTime())) {
      return null;
    }

    const updatedDiffTime = now.getTime() - updatedDate.getTime();
    const remainingDays =
      7 - Math.ceil(updatedDiffTime / (1000 * 60 * 60 * 24));

    if (remainingDays > 0) {
      return `I will finish in ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
    }
  }

  return `I will finish in 1 day`;
};

interface RequiredFields {
  [key: string]: {
    [key: string]: boolean;
  };
}

interface CompletionResult {
  percentage: number;
  missingFields: string[];
}

export function calculateCompletionPercentage(
  data: GrantApplication
): CompletionResult {
  const requiredFields: RequiredFields = {
    organizationInfo: {
      applicantName: true,
      organizationName: true,
      mission: true,
      story: true,
      where: true,
    },
    grantInfo: {
      foundationName: true,
      mission: true,
    },
    programInfo: {
      programName: true,
      programTotalCost: true,
      amountAsking: true,
    },
  };

  let totalFields = 0;
  let completedFields = 0;
  let missingFields: string[] = [];

  const isFieldCompleted = (value: unknown): boolean => {
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'string') return value.length > 0;
    return value !== null && value !== undefined;
  };

  const formatFieldName = (fieldName: string): string => {
    const withSpaces = fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .split(' ')
      .filter((word) => word.length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return withSpaces.trim();
  };

  for (const section in requiredFields) {
    if (Object.prototype.hasOwnProperty.call(requiredFields, section)) {
      const sectionKey = section as keyof Pick<
        GrantApplication,
        'organizationInfo' | 'grantInfo' | 'programInfo'
      >;
      const sectionData = data[sectionKey];

      if (!sectionData) {
        // If the entire section is missing, add all its fields as missing
        Object.keys(requiredFields[section]).forEach((field) => {
          totalFields++;
          missingFields.push(`${formatFieldName(field)}`);
        });
        continue;
      }

      for (const field in requiredFields[section]) {
        if (
          Object.prototype.hasOwnProperty.call(requiredFields[section], field)
        ) {
          totalFields++;

          const fieldValue = sectionData[field as keyof typeof sectionData];

          if (!fieldValue || !isFieldCompleted(fieldValue)) {
            missingFields.push(`${formatFieldName(field)}`);
          } else {
            completedFields++;
          }
        }
      }
    }
  }

  const percentage = Math.round((completedFields / totalFields) * 100);

  return {
    percentage,
    missingFields,
  };
}

export function countWords(text?: string) {
  if (!text || typeof text !== 'string') return 0;

  // Remove extra whitespace and split by remaining whitespace
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}
